<template>
  <EdroneTooltip :disabled="!tooltip" :helper="tooltip" :offset="[0, 8]" position="top" trigger-event="mouseenter">
    <div
      :class="{
        'tw:body-2': size === 'normal',
        'tw:caption': size === 'small',
        'tw:cursor-not-allowed': disabled,
        ...shapeStyles,
        ...solidOrNormalStyle,
      }"
      class="tw:items-center tw:flex tw:rounded-sm tw:text-nowrap"
    >
      <FontAwesomeIcon
        v-if="icon && shape !== 'rounded'"
        :class="{
          'tw:size-3.5': size === 'normal',
          'tw:size-2.5': size === 'small',
          ...disabledOrNormalIcon,
        }"
        :icon="icon"
      />
      <span v-if="shape === 'default' && label" :class="{ 'tw:ml-1': !!icon }" class="tw:text-[inherit]">
        {{ label }}
      </span>
    </div>
  </EdroneTooltip>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import EdroneTooltip from "edrone-components/EdroneTooltip.vue";
import { computed } from "vue";

export type EdroneBadgeType =
  | "info"
  | "success"
  | "warning"
  | "error"
  | "purple"
  | "violet"
  | "default"
  | "orange"
  | "teal";
export type EdroneBadgeSize = "small" | "normal";
export type EdroneBadgeShape = "default" | "square" | "rounded";
export type EdroneBadgeEmphasis = "high" | "low" | "none";

interface Props {
  emphasis?: EdroneBadgeEmphasis;
  type?: EdroneBadgeType;
  size?: EdroneBadgeSize;
  shape?: EdroneBadgeShape;
  disabled?: boolean;
  icon?: string;
  label?: string;
  tooltip?: string;
}
const props = withDefaults(defineProps<Props>(), {
  emphasis: "low",
  type: "default",
  size: "normal",
  disabled: false,
  icon: undefined,
  shape: "default",
  label: undefined,
  tooltip: undefined,
});

const solidBackgroundMap = {
  default: "tw:bg-default-900 tw:text-white",
  info: "tw:bg-blue-500 tw:text-white",
  success: "tw:bg-green-500 tw:text-white",
  warning: "tw:bg-yellow-500 tw:text-white",
  error: "tw:bg-red-500 tw:text-white",
  orange: "tw:bg-orange-500 tw:text-white",
  purple: "tw:bg-purple-500 tw:text-white",
  violet: "tw:bg-violet-500 tw:text-white",
  teal: "tw:bg-teal-500 tw:text-white",
} as const;

const normalStyleMap = {
  default: "tw:bg-default-100 tw:text-default-800",
  info: "tw:bg-blue-100 tw:text-blue-800",
  success: "tw:bg-green-100 tw:text-green-800",
  warning: "tw:bg-yellow-100 tw:text-yellow-800",
  error: "tw:bg-red-100 tw:text-red-800",
  orange: "tw:bg-orange-100 tw:text-orange-800",
  purple: "tw:bg-purple-100 tw:text-purple-800",
  violet: "tw:bg-violet-100 tw:text-violet-800",
  teal: "tw:bg-teal-100 tw:text-teal-800",
} as const;

const normalDisabledStyleMap = {
  default: "tw:bg-default-100 tw:text-default-500",
  info: "tw:bg-blue-100 tw:text-blue-200",
  success: "tw:bg-green-100 tw:text-green-300",
  warning: "tw:bg-yellow-100 tw:text-yellow-300",
  error: "tw:bg-red-100 tw:text-red-200",
  orange: "tw:bg-orange-100 tw:text-orange-300",
  purple: "tw:bg-purple-100 tw:text-purple-200",
  violet: "tw:bg-violet-100 tw:text-violet-200",
  teal: "tw:bg-teal-100 tw:text-teal-200",
} as const;

const iconColorMap = {
  default: "tw:text-default-500",
  info: "tw:text-blue-500",
  success: "tw:text-green-500",
  warning: "tw:text-yellow-500",
  error: "tw:text-red-500",
  orange: "tw:text-orange-500",
  purple: "tw:text-purple-500",
  violet: "tw:text-violet-500",
  teal: "tw:text-teal-500",
} as const;

const iconDisabledColorMap = {
  default: "tw:text-default-200",
  info: "tw:text-blue-200",
  success: "tw:text-green-300",
  warning: "tw:text-yellow-300",
  error: "tw:text-red-200",
  orange: "tw:text-orange-300",
  purple: "tw:text-purple-200",
  violet: "tw:text-violet-200",
  teal: "tw:text-teal-200",
} as const;

const noneEmphasisStyleMap = {
  default: "tw:text-default-800",
  info: "tw:text-blue-800",
  success: "tw:text-green-800",
  warning: "tw:text-yellow-800",
  error: "tw:text-red-800",
  orange: "tw:text-orange-800",
  purple: "tw:text-purple-800",
  violet: "tw:text-violet-800",
  teal: "tw:text-teal-800",
} as const;

const solidOrNormalStyle = computed(() => {
  if (props.disabled) {
    return { [normalDisabledStyleMap[props.type]]: true };
  }

  if (props.emphasis === "high") {
    return { [solidBackgroundMap[props.type]]: true };
  }

  if (props.emphasis === "low") {
    return { [normalStyleMap[props.type]]: true };
  }

  // emphasis === "none"
  return { [noneEmphasisStyleMap[props.type]]: true };
});

const disabledOrNormalIcon = computed(() => {
  if (props.disabled) {
    return { [iconDisabledColorMap[props.type]]: true };
  }

  if (props.emphasis === "high") {
    return { "tw:text-white": true };
  }

  return { [iconColorMap[props.type]]: true };
});

const shapeStyles = computed(() => {
  if (props.size === "normal") {
    return {
      "tw:px-3 tw:py-1.5 tw:w-max": props.shape === "default",
      "tw:size-8 tw:justify-center": props.shape === "square",
      "tw:size-4 tw:justify-center tw:rounded-full": props.shape === "rounded",
    };
  }

  return {
    "tw:px-1.5 tw:py-1 tw:w-max": props.shape === "default",
    "tw:size-6 tw:justify-center": props.shape === "square",
    "tw:size-3 tw:justify-center tw:rounded-full": props.shape === "rounded",
  };
});
</script>
